import { useCallback, useEffect, useState } from "react";
import {
  App as AntdApp,
  Button,
  Card,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { apiClient } from "../../../services/api/apiClient";
import { PageHeader } from "../../../components/common/PageHeader";
import { usePageTitle } from "../../../hooks";

const { Text } = Typography;

interface DivergentRow {
  id: number;
  name: string;
  audit: {
    id: number | null;
    start_date: string | null;
    auditor_name: string | null;
  };
  farm: { id: number | null; name: string | null };
  producer: { id: number | null; name: string | null };
  plot_real: {
    id: number;
    variety_id: number | null;
    variety_code: string | null;
    variety_name: string | null;
  };
  audit_plot: {
    variety_id: number | null;
    variety_code: string | null;
    variety_name: string | null;
  };
}

/**
 * Admin page: bandeja de divergencias de variedad entre `AuditPlot` y
 * su `PlotReal` enlazada. Cuando un auditor captura una variedad
 * distinta de la canónica, la fila aparece aquí. El coordinador
 * decide:
 *
 *  - "Confirmar" → la canónica del PlotReal se actualiza a la
 *    observada y queda registro en `PlotRealRevision`.
 *  - "Rechazar" → la AuditPlot se realinea a la canónica (asumimos
 *    que el auditor se equivocó). La canónica no se mueve.
 */
export const VarietyConfirmationsList = () => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();
  const [data, setData] = useState<DivergentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<number | null>(null);

  usePageTitle(
    t("variety_confirmations.title", "Confirmar variedad"),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<{ count: number; results: DivergentRow[] }>(
        "/admin/variety-confirmations/",
      );
      setData(r.data?.results ?? []);
    } catch {
      notify.error(
        t(
          "variety_confirmations.load_error",
          "Error al cargar las divergencias de variedad",
        ),
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const confirm = async (row: DivergentRow) => {
    setActingId(row.id);
    try {
      await apiClient.post(
        `/admin/variety-confirmations/${row.id}/confirm/`,
        {},
      );
      notify.success(
        t(
          "variety_confirmations.confirm_success",
          "Variedad canónica actualizada y revisión creada",
        ),
      );
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t(
          "variety_confirmations.confirm_error",
          "No se pudo confirmar la variedad",
        );
      notify.error(msg);
    } finally {
      setActingId(null);
    }
  };

  const reject = async (row: DivergentRow) => {
    setActingId(row.id);
    try {
      await apiClient.post(
        `/admin/variety-confirmations/${row.id}/reject/`,
        {},
      );
      notify.success(
        t(
          "variety_confirmations.reject_success",
          "AuditPlot realineada con la variedad canónica",
        ),
      );
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t(
          "variety_confirmations.reject_error",
          "No se pudo rechazar la divergencia",
        );
      notify.error(msg);
    } finally {
      setActingId(null);
    }
  };

  const columns = [
    {
      title: t("variety_confirmations.producer", "Productor"),
      dataIndex: ["producer", "name"],
      key: "producer",
      ellipsis: true,
      render: (_: any, row: DivergentRow) => row.producer.name || "—",
    },
    {
      title: t("variety_confirmations.farm", "Finca"),
      dataIndex: ["farm", "name"],
      key: "farm",
      ellipsis: true,
      render: (_: any, row: DivergentRow) => row.farm.name || "—",
    },
    {
      title: t("variety_confirmations.audit_plot", "Parcela auditada"),
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: t("variety_confirmations.audit", "Auditoría"),
      key: "audit",
      width: 180,
      render: (_: any, row: DivergentRow) => {
        const date = row.audit.start_date
          ? dayjs(row.audit.start_date).format("DD/MM/YYYY")
          : "—";
        return (
          <Space direction="vertical" size={0}>
            <Text>{date}</Text>
            {row.audit.auditor_name && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.audit.auditor_name}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: t("variety_confirmations.canonical", "Variedad canónica"),
      key: "canonical",
      width: 160,
      render: (_: any, row: DivergentRow) => (
        <Tag color="blue">{row.plot_real.variety_name || "—"}</Tag>
      ),
    },
    {
      title: t("variety_confirmations.observed", "Variedad observada"),
      key: "observed",
      width: 160,
      render: (_: any, row: DivergentRow) => (
        <Tag color="warning" icon={<ExclamationCircleOutlined />}>
          {row.audit_plot.variety_name || "—"}
        </Tag>
      ),
    },
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      width: 260,
      align: "right" as const,
      render: (_: any, row: DivergentRow) => (
        <Space>
          <Popconfirm
            title={t(
              "variety_confirmations.confirm_title",
              "Actualizar variedad canónica",
            )}
            description={t(
              "variety_confirmations.confirm_description",
              "Se actualizará la variedad de la parcela real y quedará registrada en PlotRealRevision. Esta acción es auditable.",
            )}
            onConfirm={() => confirm(row)}
            okText={t("common.yes", "Sí")}
            cancelText={t("common.no", "No")}
            okButtonProps={{ icon: <CheckOutlined /> }}
          >
            <Tooltip
              title={t(
                "variety_confirmations.confirm_tooltip",
                "Promocionar la variedad observada a canónica",
              )}
            >
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                loading={actingId === row.id}
              >
                {t("variety_confirmations.confirm", "Confirmar")}
              </Button>
            </Tooltip>
          </Popconfirm>
          <Popconfirm
            title={t(
              "variety_confirmations.reject_title",
              "Realinear con la canónica",
            )}
            description={t(
              "variety_confirmations.reject_description",
              "Asumimos que la variedad observada fue un error del auditor. La parcela auditada se actualiza a la variedad canónica.",
            )}
            onConfirm={() => reject(row)}
            okText={t("common.yes", "Sí")}
            cancelText={t("common.no", "No")}
            okButtonProps={{ danger: true, icon: <CloseOutlined /> }}
          >
            <Tooltip
              title={t(
                "variety_confirmations.reject_tooltip",
                "Mantener la canónica y descartar la divergencia",
              )}
            >
              <Button size="small" icon={<CloseOutlined />} danger>
                {t("variety_confirmations.reject", "Rechazar")}
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <PageHeader
        icon={<ExclamationCircleOutlined />}
        accent="#FA8C16"
        title={t("variety_confirmations.title", "Confirmar variedad")}
        subtitle={t(
          "variety_confirmations.subtitle",
          "Parcelas auditadas cuya variedad observada no coincide con la canónica del PlotReal. Confirma para propagar el cambio (queda revisión histórica) o rechaza para mantener la canónica.",
        )}
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => void load()}>
            {t("common.reload", "Refrescar")}
          </Button>
        }
      />

      <Table<DivergentRow>
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 1100 }}
      />
    </Card>
  );
};

export default VarietyConfirmationsList;
